import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPaperPlane, FaPaperclip, FaUserCircle, FaTrash } from 'react-icons/fa';
import BackButton from '../components/BackButton';
import { 
  getQuestionById, 
  updateQuestion, 
  getQuestionMessages, 
  addQuestionMessage,
  subscribeToQuestionMessages,
  deleteQuestion
} from '../services/firebase/questions';
import { getInternalUsers } from '../services/firebase/users';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';

// Deterministic gradient per user so initials avatars are distinct and readable.
const AVATAR_GRADIENTS = [
  'from-rose-500 to-red-600',
  'from-amber-500 to-orange-600',
  'from-emerald-500 to-green-600',
  'from-sky-500 to-blue-600',
  'from-violet-500 to-purple-600',
  'from-fuchsia-500 to-pink-600',
  'from-cyan-500 to-teal-600',
  'from-indigo-500 to-blue-700',
];
const gradientForKey = (key = '') => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

const initialsFor = (firstName, lastName, name) => {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName.substring(0, 2).toUpperCase();
  return (name || 'U').substring(0, 2).toUpperCase();
};

// Resolve a sender to a display profile: users + internal_users -> users by accountId -> accounts
const fetchUserProfile = async (userId) => {
  try {
    // A CRM user has a doc in BOTH collections: `users` (with isInternal) holds their
    // details, but the profile picture is saved to `internal_users` by CRM Profile.
    // Read both and merge so the image is picked up wherever it lives.
    const [userDoc, internalUserDoc] = await Promise.all([
      getDoc(doc(db, 'users', userId)),
      getDoc(doc(db, 'internal_users', userId)),
    ]);

    if (userDoc.exists() || internalUserDoc.exists()) {
      const userData = userDoc.exists() ? userDoc.data() : {};
      const internalData = internalUserDoc.exists() ? internalUserDoc.data() : {};

      const firstName = userData.firstName || internalData.firstName || '';
      const lastName = userData.lastName || internalData.lastName || '';
      const name = `${firstName} ${lastName}`.trim()
        || userData.displayName
        || internalData.name
        || userData.email?.split('@')[0]
        || 'User';

      return {
        id: userId,
        name,
        initials: initialsFor(firstName, lastName, name),
        // Only use profileImage from My Profile, not from account/contact data
        profileImage: userData.profileImage || internalData.profileImage || null,
      };
    }

    // The id may be an accountId rather than a user id
    const usersByAccount = await getDocs(
      query(collection(db, 'users'), where('accountId', '==', userId))
    );
    if (!usersByAccount.empty) {
      const userData = usersByAccount.docs[0].data();
      const name = `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
        || userData.displayName || 'User';
      return {
        id: usersByAccount.docs[0].id,
        name,
        initials: initialsFor(userData.firstName, userData.lastName, name),
        profileImage: userData.profileImage || null,
      };
    }

    const accountDoc = await getDoc(doc(db, 'accounts', userId));
    if (accountDoc.exists()) {
      const accountData = accountDoc.data();
      const contact = accountData.contact || {};
      const firstName = contact.firstName || contact.name?.split(' ')[0] || '';
      const lastName = contact.lastName || contact.name?.split(' ').slice(1).join(' ') || '';
      const name = contact.name || `${firstName} ${lastName}`.trim() || accountData.company || 'Client';
      return {
        id: userId,
        name,
        initials: initialsFor(firstName, lastName, accountData.company || name),
        profileImage: null,
      };
    }
  } catch (error) {
    console.error('Error loading sender profile:', error);
  }

  return null;
};

// Avatar component for users — mirrors the one on /crm/messages
const UserAvatar = ({ user = {}, size = 'sm', className = '' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-[11px]',
    md: 'w-10 h-10 text-sm',
  };

  const initials = user.initials || (user.name ? user.name.substring(0, 2).toUpperCase() : '?');
  const gradient = gradientForKey(user.id || user.name || initials);
  const baseClasses = `rounded-full flex items-center justify-center font-semibold select-none shrink-0 ${sizeClasses[size]} ${className}`;

  if (user.profileImage) {
    return (
      <div className={`${baseClasses} overflow-hidden`}>
        <img
          src={user.profileImage}
          alt={user.name || 'User'}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className={`w-full h-full items-center justify-center bg-gradient-to-br ${gradient} text-white`} style={{ display: 'none' }}>
          {initials}
        </div>
      </div>
    );
  }

  return (
    <div className={`${baseClasses} bg-gradient-to-br ${gradient} text-white`}>
      {initials}
    </div>
  );
};

const QuestionDetail = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State
  const [question, setQuestion] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [internalUsers, setInternalUsers] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [clientName, setClientName] = useState('');
  const [senderProfiles, setSenderProfiles] = useState({});

  // Load question and messages on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load question details
        const questionData = await getQuestionById(questionId);
        setQuestion(questionData);
        
        // Load messages
        const messagesData = await getQuestionMessages(questionId);
        setMessages(messagesData);
        
        // Load internal users for assignee selection
        const usersData = await getInternalUsers();
        setInternalUsers(usersData);
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading question data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [questionId]);

  // Resolve the client (portal user) who submitted the question
  useEffect(() => {
    const loadClientName = async () => {
      if (!question) return;

      const fallback = question.createdByName || '';

      if (!question.createdBy) {
        setClientName(fallback);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', question.createdBy));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          setClientName(fullName || userData.displayName || fallback);
          return;
        }
      } catch (error) {
        console.error('Error loading question author:', error);
      }

      setClientName(fallback);
    };

    loadClientName();
  }, [question]);

  // Load current user profile
  useEffect(() => {
    const loadCurrentUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        // Same merged users + internal_users lookup the message avatars use
        const profile = await fetchUserProfile(currentUser.uid);

        // Role still comes from whichever doc defines it
        const [userDoc, internalUserDoc] = await Promise.all([
          getDoc(doc(db, 'users', currentUser.uid)),
          getDoc(doc(db, 'internal_users', currentUser.uid)),
        ]);
        const role = userDoc.data()?.role
          || internalUserDoc.data()?.role
          || (internalUserDoc.exists() ? 'internal' : 'client');

        setCurrentUserProfile({
          id: currentUser.uid,
          name: profile?.name || currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
          initials: profile?.initials,
          profileImage: profile?.profileImage || currentUser.photoURL,
          role
        });
      } catch (error) {
        console.error('Error loading current user profile:', error);
        // Fallback to basic user info
        setCurrentUserProfile({
          id: currentUser.uid,
          name: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
          profileImage: currentUser.photoURL,
          role: 'user'
        });
      }
    };

    loadCurrentUserProfile();
  }, [currentUser]);

  // Load profiles for everyone who has posted a message (same lookup chain as /crm/messages)
  useEffect(() => {
    const loadSenderProfiles = async () => {
      const missingIds = [...new Set(
        messages.map(message => message.senderId).filter(Boolean)
      )].filter(senderId => !(senderId in senderProfiles));

      if (missingIds.length === 0) return;

      const loaded = {};

      await Promise.all(missingIds.map(async (senderId) => {
        loaded[senderId] = await fetchUserProfile(senderId);
      }));

      setSenderProfiles(prev => ({ ...prev, ...loaded }));
    };

    loadSenderProfiles();
  }, [messages, senderProfiles]);

  // Set up real-time listener for messages
  useEffect(() => {
    if (questionId) {
      const unsubscribe = subscribeToQuestionMessages(questionId, (messagesData) => {
        setMessages(messagesData);
      });

      return () => unsubscribe();
    }
  }, [questionId]);

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    if (!question || newStatus === question.status) return;
    
    try {
      setIsUpdating(true);
      const updateData = { status: newStatus };
      
      // If reopening a question, clear the resolved date
      if (newStatus === 'Open' && question.status === 'Resolved') {
        updateData.resolvedAt = null;
      }
      
      await updateQuestion(questionId, updateData);
      setQuestion(prev => ({ 
        ...prev, 
        status: newStatus,
        resolvedAt: newStatus === 'Open' ? null : prev.resolvedAt
      }));
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdating(false);
    }
  };



  // Handle sending message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUserProfile) return;

    try {
      await addQuestionMessage(questionId, {
        senderId: currentUserProfile.id,
        senderName: currentUserProfile.name,
        senderRole: currentUserProfile.role,
        messageText: newMessage.trim(),
        attachments: []
      });

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  // Handle resolving question
  const handleResolveQuestion = async () => {
    if (!question || question.status === 'Resolved') return;
    
    try {
      await updateQuestion(questionId, { 
        status: 'Resolved',
        resolvedAt: new Date()
      });
      setQuestion(prev => ({ 
        ...prev, 
        status: 'Resolved',
        resolvedAt: new Date()
      }));
    } catch (error) {
      console.error('Error resolving question:', error);
      alert('Failed to resolve question. Please try again.');
    }
  };

  // Handle deleting question
  const handleDeleteQuestion = async () => {
    if (!question) return;
    
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this question?\n\n"${question.questionText.substring(0, 100)}${question.questionText.length > 100 ? '...' : ''}"\n\nThis action cannot be undone and will also delete all associated messages.`
    );
    
    if (!confirmDelete) return;
    
    try {
      setIsDeleting(true);
      await deleteQuestion(questionId);
      navigate('/crm/questions');
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Failed to delete question. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6 min-h-screen relative">
        <PortalBackground />
        <div className="relative flex items-center justify-center h-full">
          <BBALoader size={48} className="mx-auto" />
        </div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="container mx-auto px-4 py-6 min-h-screen relative">
        <PortalBackground />
        <div className="relative text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Question not found</h2>
          <button 
            onClick={() => navigate('/crm/questions')}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Back to Questions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 min-h-screen relative">
      {/* Header */}
      <PortalBackground />
      <div className="relative">
      <div className="flex items-center justify-between mb-6">
        <BackButton onClick={() => navigate('/crm/questions')} />
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Question Details</h1>
        <button
          onClick={handleDeleteQuestion}
          disabled={isDeleting}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors"
        >
          <FaTrash className="h-4 w-4" />
          {isDeleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Question Details */}
        <div className="lg:col-span-1">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
                         <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 text-center">Question Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company
                </label>
                <p className="text-sm text-gray-900 dark:text-gray-100">{question.companyName}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Client
                </label>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {clientName || 'Unknown'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-full ${
                  question.status === 'Open' ? 'bg-yellow-100 dark:bg-yellow-900' :
                  question.status === 'In Progress' ? 'bg-orange-100 dark:bg-orange-900' :
                  'bg-green-100 dark:bg-green-900'
                }`}>
                  <span className={`text-sm font-medium ${
                    question.status === 'Open' ? 'text-yellow-800 dark:text-yellow-200' :
                    question.status === 'In Progress' ? 'text-orange-800 dark:text-orange-200' :
                    'text-green-800 dark:text-green-200'
                  }`}>
                    {question.status}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Assigned To
                </label>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {question.assigneeName || 'Unassigned'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Created
                </label>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {question.createdAt ? new Date(question.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'N/A'}
                </p>
              </div>

                             {question.status === 'Resolved' && question.resolvedAt && (
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     Resolved
                   </label>
                   <p className="text-sm text-gray-900 dark:text-gray-100">
                     {new Date(question.resolvedAt).toLocaleDateString('en-US', {
                       year: 'numeric',
                       month: 'long',
                       day: 'numeric',
                       hour: '2-digit',
                       minute: '2-digit'
                     })}
                   </p>
                 </div>
               )}

                             {/* Status Update Button */}
               {question.status === 'Open' && (
                 <div className="pt-4">
                   <button
                     onClick={() => handleStatusUpdate('In Progress')}
                     className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors"
                   >
                     Mark As In Progress
                   </button>
                 </div>
               )}
               {question.status === 'In Progress' && (
                 <div className="pt-4">
                   <button
                     onClick={handleResolveQuestion}
                     className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                   >
                     Mark as Resolved
                   </button>
                 </div>
               )}
               {question.status === 'Resolved' && (
                 <div className="pt-4">
                   <button
                     onClick={() => handleStatusUpdate('Open')}
                     className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                   >
                     Reopen Question
                   </button>
                 </div>
               )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="lg:col-span-2">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
            {/* Question Text */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Question</h3>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{question.questionText}</p>
            </div>

            {/* Messages List */}
            <div className="p-6 max-h-96 overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Messages</h3>
              
              {messages.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">No messages yet</p>
              ) : (
                <div className="space-y-4">
                  {messages.map(message => {
                    const isCurrentUser = message.senderId === currentUserProfile?.id;
                    const senderProfile = senderProfiles[message.senderId];
                    const senderInfo = {
                      id: message.senderId,
                      name: senderProfile?.name || message.senderName || 'User',
                      initials: senderProfile?.initials,
                      profileImage: senderProfile?.profileImage
                        || (isCurrentUser ? currentUserProfile?.profileImage : null),
                    };
                    return (
                      <div key={message.id} className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                        <div className="flex-shrink-0">
                          <UserAvatar user={senderInfo} size="sm" />
                        </div>
                        <div className={`flex-1 ${isCurrentUser ? 'text-right' : ''}`}>
                          <div className={`flex items-center gap-2 mb-1 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {message.senderName}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {message.timestamp ? new Date(message.timestamp).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'N/A'}
                            </span>
                          </div>
                          <div className={`inline-block p-3 rounded-lg ${
                            isCurrentUser 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                          }`}>
                            <p className="whitespace-pre-wrap">{message.messageText}</p>
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs opacity-75">Attachments:</p>
                                <ul className="text-xs">
                                  {message.attachments.map((attachment, idx) => (
                                    <li key={idx}>{attachment.name}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* New Message Form */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <form onSubmit={handleSendMessage} className="flex gap-3">
                <div className="flex-1">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                    rows="3"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    className="p-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <FaPaperclip className="h-5 w-5" />
                  </button>
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="p-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg"
                  >
                    <FaPaperPlane className="h-5 w-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default QuestionDetail; 